/**
 * Documents shelf helpers — checks before upload (Express checks again) and
 * the words on each row.
 */

import {
  FOOD_SAFETY_DOCUMENT_CATEGORY,
  FOOD_SAFETY_DOCUMENT_CATEGORY_LABELS,
  FOOD_SAFETY_DOCUMENT_EXTENSIONS,
  FOOD_SAFETY_DOCUMENT_MAX_BYTES,
  FOOD_SAFETY_DOCUMENT_TITLE_MAX_LENGTH,
} from 'src/constants/foodSafety'

export const FOOD_SAFETY_DOCUMENT_ACCEPT = FOOD_SAFETY_DOCUMENT_EXTENSIONS.map(
  ext => `.${ext}`,
).join(',')

export const FOOD_SAFETY_DOCUMENT_CATEGORY_OPTIONS = Object.freeze(
  Object.values(FOOD_SAFETY_DOCUMENT_CATEGORY).map(category => ({
    category,
    label: FOOD_SAFETY_DOCUMENT_CATEGORY_LABELS[category],
  })),
)

const extensionOf = name => {
  const match = /\.([a-z0-9]+)$/i.exec(String(name || '').trim())
  return match ? match[1].toLowerCase() : ''
}

/** "Rentokil visit Oct.pdf" → "Rentokil visit Oct". */
export function documentTitleFromFileName(name) {
  return String(name || '')
    .replace(/\.[a-z0-9]+$/i, '')
    .replace(/_+/g, ' ')
    .trim()
    .slice(0, FOOD_SAFETY_DOCUMENT_TITLE_MAX_LENGTH)
}

/** Error sentence for a file we won't send, or null. */
export function validateFoodSafetyDocumentFile(file) {
  if (!file) return 'Choose a file to upload.'
  if (!FOOD_SAFETY_DOCUMENT_EXTENSIONS.includes(extensionOf(file.name))) {
    return 'That file type isn’t supported — upload a PDF, photo, Word or Excel file.'
  }
  if (file.size > FOOD_SAFETY_DOCUMENT_MAX_BYTES) {
    return `That file is too big — the limit is ${Math.round(
      FOOD_SAFETY_DOCUMENT_MAX_BYTES / (1024 * 1024),
    )} MB.`
  }
  return null
}

/** "820 KB", "1.4 MB". */
export function formatFoodSafetyDocumentSize(bytes) {
  const n = Number(bytes)
  if (!Number.isFinite(n) || n <= 0) return ''
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`
  return `${(n / (1024 * 1024)).toFixed(1).replace(/\.0$/, '')} MB`
}

const formatDay = value => {
  const date = value ? new Date(value) : null
  if (!date || Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

/** `{ detail: 'Pest control · PDF · 1.2 MB', meta: 'Maria · 4 Oct 2026' }` */
export function formatFoodSafetyDocumentRow(document) {
  const ext = extensionOf(document?.fileName).toUpperCase()
  return {
    detail: [
      document?.categoryLabel ||
        FOOD_SAFETY_DOCUMENT_CATEGORY_LABELS[document?.category] ||
        'Other',
      ext,
      formatFoodSafetyDocumentSize(document?.sizeBytes),
    ]
      .filter(Boolean)
      .join(' · '),
    meta: [
      document?.uploadedByName ? `Uploaded by ${document.uploadedByName}` : '',
      formatDay(document?.uploadedAt),
    ]
      .filter(Boolean)
      .join(' · '),
  }
}

/** Filter chips with counts; "All" first. */
export function buildFoodSafetyDocumentFilters(documents = []) {
  const counts = {}
  for (const doc of documents) {
    counts[doc.category] = (counts[doc.category] || 0) + 1
  }
  return [
    { category: null, label: 'All', count: documents.length },
    ...FOOD_SAFETY_DOCUMENT_CATEGORY_OPTIONS.filter(
      o => counts[o.category],
    ).map(o => ({ ...o, count: counts[o.category] })),
  ]
}
